import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// @mui material components
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from "@mui/material/Grid";

// Vision UI Dashboard PRO React components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from 'components/VuiInput';
import VuiSelect from 'components/VuiSelect';
import VuiPagination from 'components/VuiPagination';
import Icon from '@mui/material/Icon';

// Vision UI Dashboard PRO React example components
import DataTable from "examples/Tables/DataTable";

// API Services
import { takedownsApi } from 'services/takedownsApi';

// Data transformation utilities
import { 
  transformPlatformData, 
  getPlatformFilterButtons, 
  createEmptyPlatformData 
} from 'utils/platformDataTransform';


function PlatformDetails() {
  const { platformid } = useParams();
  
  // State management
  const [activeFilter, setActiveFilter] = useState(null);
  const [platformData, setPlatformData] = useState(createEmptyPlatformData());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiParams, setApiParams] = useState({
    page: 1,
    limit: 10,
    search: undefined,
    sort_by: 'detected_date',
    sort_order: 'DESC'
  });
  const [availableFilters, setAvailableFilters] = useState([]);
  const [counts, setCounts] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [platformStats, setPlatformStats] = useState({
    total_sent: 0,
    complied: 0,
    uncomplied: 0,
    compliance_rate: 0
  });
  const [platformOverview, setPlatformOverview] = useState({
    channelType: 'Not Available',
    jurisdiction: 'Not Available',
    infringementTypes: 'Not Available',
    cdnProviders: 'Not Available',
    paymentProviders: 'Not Available',
    adNetwork: 'Not Available',
    dnsProvider: 'Not Available'
  });

  // Fetch platform data from API
  const fetchPlatformData = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const requestParams = { ...apiParams, ...params };
      setApiParams(requestParams);


      // Fetch data and counts in parallel
      const [dataResponse, countsResponse] = await Promise.all([
        takedownsApi.fetchPlatformData(platformid, requestParams),
        takedownsApi.fetchPlatformCounts(platformid, requestParams).catch(() => null) // Don't fail if counts endpoint is unavailable
      ]);


      // Transform the data for the DataTable
      const transformedData = transformPlatformData(dataResponse);
      setPlatformData(transformedData);
      setPlatformStats(dataResponse.platform_stats || platformStats);

      // Extract platform overview data from the API response
      if (dataResponse.data && dataResponse.data.length > 0) {
        // Extract unique host_names for CDN providers
        const uniqueHostNames = [...new Set(
          dataResponse.data
            .map(item => item.host_name)
            .filter(name => name && name.trim() !== '')
        )];
        
        // Extract unique channel_types
        const uniqueChannelTypes = [...new Set(
          dataResponse.data
            .map(item => item.channel_type)
            .filter(type => type && type.trim() !== '')
        )];
        
        // Extract unique host_countries for jurisdiction
        const uniqueCountries = [...new Set(
          dataResponse.data
            .map(item => item.host_country)
            .filter(country => country && country.trim() !== '')
        )];
        
        // Extract unique violation_types for infringement types
        const uniqueInfringementTypes = [...new Set(
          dataResponse.data
            .map(item => item.violation_type)
            .filter(type => type && type.trim() !== '')
        )];
        
        setPlatformOverview({
          channelType: uniqueChannelTypes.length > 0 ? uniqueChannelTypes.join(', ') : 'Not Available',
          jurisdiction: uniqueCountries.length > 0 ? uniqueCountries.join(', ') : 'Not Available',
          infringementTypes: uniqueInfringementTypes.length > 0 ? uniqueInfringementTypes.join(', ') : 'Not Available',
          cdnProviders: uniqueHostNames.length > 0 ? uniqueHostNames.join(', ') : 'Not Available',
          paymentProviders: 'Not Available', // Not yet available in API
          adNetwork: 'Not Available', // Not yet available in API
          dnsProvider: 'Not Available' // Not yet available in API
        });
      }

      // Update available filters and counts
      if (countsResponse) {
        setCounts(countsResponse);
        const filterButtons = getPlatformFilterButtons(countsResponse, handleFilterChange, activeFilter);
        setAvailableFilters(filterButtons);
      } else {
        // Fallback filter buttons if counts are not available
        setAvailableFilters([
          { key: 'requiresApproval', label: 'Requires Approval', filter: { takedown_status: 'Pending' } },
          { key: 'Explicit', label: 'Explicit', filter: { violation_type: 'Explicit' } },
          { key: 'Copyright', label: 'Copyright', filter: { violation_type: 'Copyright' } }
        ]);
      }

    } catch (err) {
      setError(err.message || 'Failed to fetch platform data');
      setPlatformData(createEmptyPlatformData());
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts only (for filter updates)
  const fetchCountsOnly = async (filters = {}) => {
    try {
      const requestParams = { ...apiParams, ...filters };
      const countsResponse = await takedownsApi.fetchPlatformCounts(platformid, requestParams);
      setCounts(countsResponse);
      
      const filterButtons = getPlatformFilterButtons(countsResponse, handleFilterChange, activeFilter);
      setAvailableFilters(filterButtons);
    } catch (err) {
      // Don't update state on error, keep existing filters
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, filterValue) => {
    setActiveFilter(filterKey);
    fetchPlatformData({ 
      page: 1, // Reset to first page when filtering
      ...filterValue,
      search: undefined // Clear search when filtering
    });
    setSearchTerm(''); // Clear search term
  };

  // Handle page changes
  const handlePageChange = (page) => {
    fetchPlatformData({ page });
  };

  // Handle search with debouncing
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      fetchPlatformData({ 
        page: 1, // Reset to first page when searching
        search: value || undefined
      });
      setActiveFilter(null); // Clear active filter when searching
    }, 500);

    setSearchTimeout(timeout);
  };

  // Handle entries per page changes
  const handleEntriesPerPageChange = (entriesPerPage) => {
    fetchPlatformData({ 
      page: 1, // Reset to first page when changing entries per page
      limit: entriesPerPage 
    });
  };

  // Load data on component mount
  useEffect(() => {
    if (platformid) {
      fetchPlatformData();
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [platformid]);

  // Get filter buttons with counts
  const getFilterButtons = () => {
    return availableFilters.map((filter) => (
      <VuiButton
        key={filter.key}
        color={activeFilter === filter.key ? 'info' : 'dark'}
        variant={activeFilter === filter.key ? 'contained' : 'outlined'}
        onClick={() => handleFilterChange(filter.key, filter.filter)}
        sx={{
          border: activeFilter !== filter.key ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
          '&:hover': {
            backgroundColor:
              activeFilter === filter.key ? '#0075FF' : 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        {filter.label}
      </VuiButton>
    ));
  };

  const handleReject = () => {
  };

  const handleHold = () => {
  };

  const handleAlertMB = () => {
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} sx={{ overflow: "visible" }}>
        {/* Header */}
        <VuiBox mb={3}>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            Platform Details - {platformid}
          </VuiTypography>
        </VuiBox>

        

        {/* Loading State */}
        {loading && (
          <VuiBox display="flex" justifyContent="center" py={4}>
            <CircularProgress color="info" />
          </VuiBox>
        )}

        {/* Error State */}
        {error && (
          <VuiBox mb={3}>
            <Alert severity="error" sx={{ backgroundColor: 'rgba(255, 107, 107, 0.1)', color: 'white' }}>
              {error}
            </Alert>
          </VuiBox>
        )}

        {/* Main Content - Only show when not loading and no error */}
        {!loading && !error && (
          <>

        <Grid container spacing={3} alignItems="stretch">
          {/* Platform Overview Card */}
          <Grid item xs={12} lg={7}>
            <Card sx={{ 
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(60px)",
              borderRadius: "20px",
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <VuiBox>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={3}>
                  Platform Overview
                </VuiTypography>

                <Grid container spacing={3}>
                  {/* General Section */}
                  <Grid item xs={12} md={4} lineHeight="1.2">
                    <VuiBox>
                      <VuiTypography 
                        variant="button" 
                        color="white" 
                        fontWeight="bold" 
                        mb={2}
                        sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                      >
                        GENERAL
                      </VuiTypography>
                      
                      <VuiBox display="flex" flexDirection="column" gap={2} lineHeight="1.2">
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            NAME
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformid}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            CHANNEL / DOMAIN
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformOverview.channelType || 'Not Available'}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            JURISDICTION
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformOverview.jurisdiction || 'Not Available'}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            INFRINGEMENT TYPE(S)
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformOverview.infringementTypes || 'Not Available'}
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </Grid>

                  {/* Technicals Section */}
                  <Grid item xs={12} md={4} lineHeight="1.2">
                    <VuiBox>
                      <VuiTypography 
                        variant="button" 
                        color="white" 
                        fontWeight="bold" 
                        mb={2}
                        sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                      >
                        TECHNICALS
                      </VuiTypography>
                      
                      <VuiBox display="flex" flexDirection="column" gap={2} lineHeight="1.2">
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            CDN PROVIDER(S)
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformOverview.cdnProviders || 'Not Available'}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            PAYMENT PROVIDER(S)
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformOverview.paymentProviders || 'Not Available'}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            AD NETWORK
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformOverview.adNetwork || 'Not Available'}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            DNS PROVIDER
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformOverview.dnsProvider || 'Not Available'}
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </Grid>

                  {/* Takedown Stats Section */}
                  <Grid item xs={12} md={4} lineHeight="1.2">
                    <VuiBox>
                      <VuiTypography 
                        variant="button" 
                        color="white" 
                        fontWeight="bold" 
                        mb={2}
                        sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                      >
                        TAKEDOWN STATS
                      </VuiTypography>
                      
                      <VuiBox display="flex" flexDirection="column" gap={2} lineHeight="1.2">
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            COMPLIANCE STATUS
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformStats.compliance_rate >= 50 ? 'Good' : platformStats.compliance_rate >= 25 ? 'Fair' : 'Poor'}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            TOTAL SENT
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformStats.total_sent}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            COMPLIED
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformStats.complied}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            UNCOMPLIED
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {platformStats.uncomplied}
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </Grid>
                </Grid>
              </VuiBox>
            </Card>
          </Grid>

          {/* Platform Assessment & Action Card */}
          <Grid item xs={12} lg={5} lineHeight="1.2">
            <Card sx={{ 
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(60px)",
              borderRadius: "20px",
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <VuiBox>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={3} lineHeight="1.2">
                  Platform Assessment & Action
                </VuiTypography>

                {/* Overview Section */}
                <VuiBox mb={3}>
                  <VuiTypography 
                    variant="button" 
                    color="white" 
                    fontWeight="bold" 
                    mb={2}
                    sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                  >
                    OVERVIEW
                  </VuiTypography>
                  
                  <VuiBox display="flex" flexDirection="column" gap={2} lineHeight="1.2">
                    <VuiBox>
                      <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                        PRIORITY
                      </VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                        {platformStats.compliance_rate < 25 ? 'High' : platformStats.compliance_rate < 50 ? 'Medium' : 'Low'}
                      </VuiTypography>
                    </VuiBox>
                    
                    <VuiBox>
                      <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                        PRIORITY REASON
                      </VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                        {platformStats.compliance_rate < 25 ? 'Low compliance rate with takedown requests' : 'Standard compliance monitoring'}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                </VuiBox>

                {/* Recommendation Section */}
                <VuiBox mb={3}>
                  <VuiTypography 
                    variant="button" 
                    color="white" 
                    fontWeight="bold" 
                    mb={2}
                    sx={{ fontSize: '14px', textTransform: 'uppercase' , lineHeight: 1.2}}
                  >
                    RECOMMENDATION
                  </VuiTypography>
                  <VuiTypography 
                    variant="body2" 
                    color="white" 
                    sx={{ fontSize: '14px', lineHeight: 1.2 }}
                  >
                    {platformStats.compliance_rate < 25 
                      ? 'As the activity increases and the platform continues to not comply with the takedown requests, we believe this platform is a great candidate to pursue further action. We recommend filing litigation against the platform, and sending initial notices to 3rd party ecosystem platforms (CDN, DNS, Ad Network).'
                      : 'Continue monitoring platform compliance. Standard takedown procedures are working effectively.'
                    }
                  </VuiTypography>
                </VuiBox>

                {/* Action Buttons */}
                {/* <VuiBox display="flex" gap={2} justifyContent="center">
                  <VuiButton
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleReject}
                    sx={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      px: 2,
                      py: 1,
                      minHeight: "24px",
                      borderColor: "#e51313",
                      color: "#e51313"
                    }}
                  >
                    REJECT
                  </VuiButton>
                  
                  <VuiButton
                    variant="outlined"
                    color="white"
                    size="small"
                    onClick={handleHold}
                    sx={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      px: 2,
                      py: 1,
                      minHeight: "24px"
                    }}
                  >
                    HOLD
                  </VuiButton>
                  
                  <VuiButton
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={handleAlertMB}
                    sx={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      px: 2,
                      py: 1,
                      minHeight: "24px"
                    }}
                  >
                    ALERT MB
                  </VuiButton>
                </VuiBox> */}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>


        {/* Takedowns Table */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12}>
            {/* Search and Filter Controls */}
        <VuiBox mb={3} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {/* Search Input */}
          <VuiBox width="300px">
            <VuiInput
              placeholder="Search platform content..."
              value={searchTerm}
              onChange={handleSearch}
              icon={{
                component: "search",
                direction: "left",
              }}
            />
          </VuiBox>

          {/* Filter Buttons */}
          <VuiBox display="flex" gap={2}>
            {getFilterButtons()}
          </VuiBox>

          {/* Clear Filter Button - Only show when there are active filters */}
          {(activeFilter || searchTerm) && (
            <VuiButton
              color="dark"
              variant="outlined"
              onClick={() => {
                setActiveFilter(null);
                // Clear all filters and reset to page 1
                fetchPlatformData({ 
                  page: 1,
                  takedown_status: undefined,
                  violation_type: undefined,
                  media_type: undefined,
                  search: undefined
                });
                // Clear search term
                setSearchTerm('');
              }}
              sx={{
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Clear Filter
            </VuiButton>
          )}
        </VuiBox>
            <Card sx={{ overflow: "visible" }}>
              {/* Data Table */}
              <VuiBox>
                <DataTable
                      table={platformData}
                  entriesPerPage={{
                        defaultValue: apiParams.limit,
                    entries: [10, 20, 50, 100],
                  }}
                      canSearch={false} // Disable built-in search
                  showTotalEntries={true}
                  isSorted={true}
                  noEndBorder={false}
                  pagination={{
                    variant: 'contained',
                    color: 'info',
                        currentPage: platformData.pagination?.current_page || 1,
                        totalPages: platformData.pagination?.total_pages || 1,
                        totalItems: platformData.pagination?.total_items || 0,
                  }}
                      onPageChange={handlePageChange}
                      onEntriesPerPageChange={handleEntriesPerPageChange}
                />
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
          </>
        )}
      </VuiBox>
    </DashboardLayout>
  );
}

export default PlatformDetails;
